import { useQuery } from "@tanstack/react-query";
import { Alert, Card, Col, DatePicker, Row, Segmented, Space, Statistic, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { formatCurrency } from "../../shared/utils/formatters";
import { type FinancialReportPeriod, operationsApi } from "./operations-api";

export function ReportsTestPage() {
  const { locale } = useAppConfig();
  const zh = locale === "zh";
  const [period,setPeriod] = useState<FinancialReportPeriod>("day");
  const [anchor,setAnchor] = useState(dayjs());
  const report = useQuery({
    queryKey:["financial-report",period,anchor.format("YYYY-MM-DD")],
    queryFn:() => operationsApi.financialReport(period,anchor.format("YYYY-MM-DD"))
  });
  const data = report.data;
  const finance = data?.financials;
  const periodLabel = period === "day" ? (zh ? "日报表" : "Daily") : period === "month" ? (zh ? "月报表" : "Monthly") : (zh ? "年报表" : "Yearly");
  const composition = [
    {key:"parts",name:zh ? "配件收入" : "Parts revenue",amount:finance?.partsRevenue ?? "0.00"},
    {key:"labor",name:zh ? "人工收入" : "Labor revenue",amount:finance?.laborRevenue ?? "0.00"},
    {key:"other",name:zh ? "其他收入 / 折扣调整" : "Other revenue / adjustments",amount:finance?.otherRevenue ?? "0.00"},
    {key:"tax",name:zh ? "代收销售税（不计收入）" : "Sales tax collected (excluded from revenue)",amount:finance?.salesTax ?? "0.00"}
  ];

  return <section>
    <div className="page-title business-page-title">
      <div>
        <Typography.Title level={2}>{zh ? "经营报表" : "Business reports"}</Typography.Title>
        <Typography.Paragraph type="secondary">
          {zh ? "按门店时区汇总发票、收款、配件成本与利润指标。" : "Invoice, payment, parts cost, and profitability metrics summarized in the shop timezone."}
        </Typography.Paragraph>
      </div>
      <Space wrap>
        <Segmented
          value={period}
          onChange={value => setPeriod(value as FinancialReportPeriod)}
          options={[
            {value:"day",label:zh ? "日报表" : "Daily"},
            {value:"month",label:zh ? "月报表" : "Monthly"},
            {value:"year",label:zh ? "年报表" : "Yearly"}
          ]}
        />
        <DatePicker
          allowClear={false}
          picker={period === "day" ? "date" : period}
          value={anchor}
          onChange={value => value && setAnchor(value)}
        />
      </Space>
    </div>
    {report.isError && <Alert type="error" showIcon message={String(report.error)} />}
    <Alert
      type="warning"
      showIcon
      className="operations-report-notice"
      message={zh ? "净利润为经营估算" : "Net profit is an operating estimate"}
      description={zh
        ? "当前系统尚未记录技师工资、房租、水电、手续费及其他期间费用，因此估算净利润等于净销售额减已知配件成本，不作为会计报税数据。"
        : "Technician payroll, rent, utilities, processing fees, and other operating expenses are not tracked yet. Estimated net profit is net sales minus known parts cost and is not tax-accounting data."}
    />
    <div className="operations-report-period">
      <Tag color="blue">{periodLabel}</Tag>
      <Typography.Text type="secondary">
        {data ? `${data.period.startDate} → ${data.period.endDateExclusive} · ${data.period.timezone}` : (zh ? "正在加载报表期间…" : "Loading report period…")}
      </Typography.Text>
    </div>
    <Row gutter={[16,16]} className="operations-report-grid">
      <Col xs={24} sm={12} xl={6}><Card loading={report.isLoading}><Statistic title={zh ? "净销售收入" : "Net sales revenue"} value={Number(finance?.revenue ?? 0)} formatter={formatCurrency} /></Card></Col>
      <Col xs={24} sm={12} xl={6}><Card loading={report.isLoading}><Statistic title={zh ? "已收款" : "Collected"} value={Number(finance?.collected ?? 0)} formatter={formatCurrency} /></Card></Col>
      <Col xs={24} sm={12} xl={6}><Card loading={report.isLoading}><Statistic title={zh ? "配件成本" : "Parts cost"} value={Number(finance?.partsCost ?? 0)} formatter={formatCurrency} /></Card></Col>
      <Col xs={24} sm={12} xl={6}><Card loading={report.isLoading}><Statistic title={zh ? "人工收入" : "Labor revenue"} value={Number(finance?.laborRevenue ?? 0)} formatter={formatCurrency} /></Card></Col>
      <Col xs={24} sm={12} xl={6}><Card loading={report.isLoading}><Statistic title={zh ? "经营毛利" : "Gross profit"} value={Number(finance?.grossProfit ?? 0)} formatter={formatCurrency} /></Card></Col>
      <Col xs={24} sm={12} xl={6}><Card loading={report.isLoading}><Statistic title={zh ? "收入毛利率" : "Gross margin"} value={Number(finance?.grossMarginPercent ?? 0)} precision={2} suffix="%" /></Card></Col>
      <Col xs={24} sm={12} xl={6}><Card loading={report.isLoading}><Statistic title={zh ? "估算净利润" : "Estimated net profit"} value={Number(finance?.estimatedNetProfit ?? 0)} formatter={formatCurrency} /></Card></Col>
      <Col xs={24} sm={12} xl={6}><Card loading={report.isLoading}><Statistic title={zh ? "未收余额" : "Outstanding"} value={Number(finance?.outstanding ?? 0)} formatter={formatCurrency} /></Card></Col>
    </Row>
    {Boolean(data?.costCoverage.unmappedPartCostLines) && <Alert
      type="warning"
      showIcon
      message={zh ? `${data?.costCoverage.unmappedPartCostLines} 条配件收入无法匹配成本` : `${data?.costCoverage.unmappedPartCostLines} parts lines have no matched cost`}
    />}
    <Row gutter={[16,16]}>
      <Col xs={24} xl={16}>
        <Card title={zh ? "收入构成" : "Revenue composition"}>
          <Table
            rowKey="key"
            loading={report.isLoading}
            dataSource={composition}
            pagination={false}
            columns={[
              {title:zh ? "类别" : "Category",dataIndex:"name",key:"name"},
              {title:zh ? "金额" : "Amount",dataIndex:"amount",key:"amount",align:"right",render:formatCurrency}
            ]}
          />
        </Card>
      </Col>
      <Col xs={24} xl={8}>
        <Card title={zh ? "业务数量" : "Business activity"}>
          <Statistic title={zh ? "开具发票" : "Invoices issued"} value={data?.activity.invoiceCount ?? 0} />
          <Statistic title={zh ? "收款笔数" : "Completed payments"} value={data?.activity.paymentCount ?? 0} className="operations-stacked-statistic" />
        </Card>
      </Col>
    </Row>
  </section>;
}
